<script setup lang="ts">
import Server from '@/core/resources/Server'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useStore } from '@/core/store'
import RemoteFileListItem from '@/components/browser/RemoteFileListItem.vue'
import RemoteFileTreeItem from '@/components/browser/RemoteFileTreeItem.vue'
import RemoteFile from '@/core/resources/RemoteFile'
import { serverService } from '@/core/services'
import { handleError } from '@/utils/ErrorHandler'
import { popupUtil } from '@/utils/Popup'
import { downloadFileNewTab } from '@/utils/fileUtil'

const store = useStore()
const files = ref<RemoteFile[]>([])
const rootFolder = ref<RemoteFile | null>(null)
const path = ref<string>('/')
const filterHidden = ref<boolean>(true)
const selectedMap = ref<{ [key: string]: boolean }>({})
const selectedAll = ref<boolean>(false)

const searchTree = ref<string>('')
const searchList = ref<string>('')

const history = ref<string[]>([])
const historyIndex = ref<number>(-1)

const filteredFiles = computed(() => {
  const dotFilter = (file: RemoteFile) => (filterHidden.value ? !file.basename.startsWith('.') : true)

  const spectrumFilter = (file: RemoteFile) => !props.importSpectraMode || file.extension === '.tgz' || file.type === 'dir'

  const searchFilter = (file: RemoteFile) => searchList.value === '' || file.basename.toLowerCase().includes(searchList.value.toLowerCase())

  return files.value.filter((file) => dotFilter(file) && spectrumFilter(file) && searchFilter(file))
})

const props = defineProps({
  server: {
    type: Object as () => Server,
    required: true
  },
  importSpectraMode: {
    type: Boolean,
    default: false
  }
})

const selectedFilePaths = computed<string[]>(() => {
  return Object.keys(selectedMap.value).filter((key) => selectedMap.value[key])
})

const selectAll = (event: Event) => {
  const checked = (event.target as HTMLInputElement).checked
  filteredFiles.value.forEach((file) => {
    if (file.type !== 'dir') {
      selectedMap.value[file.path] = checked
    }
  })
}

defineExpose({
  selectedFilePaths
})

onMounted(async () => {
  const fileInput = document.getElementById('file-input') as HTMLInputElement
  fileInput.addEventListener('change', uploadFiles)

  rootFolder.value = new RemoteFile()
  rootFolder.value.path = '/'
  rootFolder.value.basename = props.server.basedir

  path.value = '/'
  history.value = ['/']
  historyIndex.value = 0
  fetchFiles()
})

const uploadFiles = async () => {
  const fileInput = document.getElementById('file-input') as HTMLInputElement
  const formData = new FormData()
  const filelist = fileInput.files!

  for (const element of filelist) {
    formData.append('files', element)
  }

  serverService
    .uploadFilesOnServer(props.server.id, path.value, formData, store.getAccessToken())
    .then(() => {
      fetchFiles()
    })
    .catch(handleError)
}

onBeforeUnmount(() => {
  const fileInput = document.getElementById('file-input') as HTMLInputElement
  fileInput.removeEventListener('change', uploadFiles)
})

const fetchFiles = async () => {
  serverService
    .getServerFiles(props.server.id, path.value, store.getAccessToken())
    .then((res) => {
      files.value = res
    })
    .catch((err) => {
      handleError(err)
      previousPath()
      history.value.pop()
    })
}

const previousPath = () => {
  if (historyIndex.value > 0) {
    historyIndex.value--
    path.value = history.value[historyIndex.value]
  }
}

const nextPath = () => {
  if (historyIndex.value < history.value.length - 1) {
    historyIndex.value++
    path.value = history.value[historyIndex.value]
  }
}

const changePath = (newPath: string) => {
  if (newPath === path.value) return

  path.value = newPath
  console.log('path changed to', path.value)

  if (historyIndex.value === history.value.length - 1) {
    history.value.push(path.value)
    historyIndex.value++
  } else {
    history.value = history.value.slice(0, historyIndex.value + 1)
    history.value.push(path.value)
    historyIndex.value++
  }
}

const pathParts = computed(() => path.value.split('/').filter((part) => part !== ''))

watch(path, async () => {
  selectedAll.value = false
  fetchFiles()
})

const downloadFile = (file: RemoteFile) => {
  serverService
    .downloadFile(props.server.id, file, store.getAccessToken())
    .then((result) => downloadFileNewTab(result, file.basename))
    .catch(handleError)
}

const mkdir = async () => {
  const name = await popupUtil.prompt('Folder name')
  if (!name) return

  serverService
    .mkdir(props.server.id, path.value, name, store.getAccessToken())
    .then(() => fetchFiles())
    .catch(handleError)
}

const deleteFile = async (file: RemoteFile) => {
  const confirmed = await popupUtil.confirm('Are you sure you want to delete this file?')
  if (!confirmed) return

  serverService
    .deleteOnServer(props.server.id, path.value, file, store.getAccessToken())
    .then(() => fetchFiles())
    .catch(handleError)
}
</script>

<template>
  <div class="browser">
    <div class="toolbar">
      <div class="toolbar__left">
        <button @click="filterHidden = !filterHidden" id="filter-hidden">
          <span class="material-symbols-outlined">
            {{ filterHidden ? 'visibility_off' : 'visibility' }}
          </span>
          <div v-if="filterHidden" class="tooltip">Show hidden files</div>
          <div v-else class="tooltip">Hide hidden files</div>
        </button>
        <button @click="previousPath" :disabled="historyIndex <= 0" id="previous-path">
          <span class="material-symbols-outlined">chevron_left</span>
        </button>
        <button @click="nextPath" :disabled="historyIndex === history.length - 1" id="next-path">
          <span class="material-symbols-outlined">chevron_right</span>
        </button>
        <div class="path">
          <button @click="changePath('/')">
            {{ server.basedir == '/' ? 'root' : server.basedir }}
          </button>
          <span v-if="pathParts.length > 0">/</span>
          <template v-for="(part, index) in pathParts" :key="index">
            <button @click="changePath('/' + pathParts.slice(0, index + 1).join('/') + '/')">
              {{ part }}
            </button>
            <span v-if="index < pathParts.length - 1">/</span>
          </template>
        </div>
      </div>

      <div class="toolbar__right">
        <label for="file-input" id="upload-files" class="button">
          <span class="material-symbols-outlined">upload_file</span>
          <div class="tooltip">Upload files</div>
        </label>
        <input type="file" multiple style="display: none" id="file-input" />
        <button @click="mkdir" id="create-folder">
          <span class="material-symbols-outlined">create_new_folder</span>
          <div class="tooltip">Create folder</div>
        </button>
        <button @click="fetchFiles" id="refresh">
          <span class="material-symbols-outlined">refresh</span>
          <div class="tooltip">Refresh</div>
        </button>
      </div>
    </div>

    <div class="browser-content">
      <div class="tree">
        <RemoteFileTreeItem
          v-if="rootFolder && server"
          :file="rootFolder"
          :server="server"
          :filterHidden="filterHidden"
          :search="searchTree"
          @changePath="changePath" />
      </div>

      <div class="files">
        <RemoteFileListItem
          v-for="file in filteredFiles"
          :key="file.path"
          :file="file"
          :selectable="props.importSpectraMode"
          @change-path="changePath"
          @download="downloadFile(file)"
          @delete="deleteFile(file)"
          v-model:selected="selectedMap[file.path]" />
      </div>
      <div class="low-toolbar">
        <input type="text" v-model="searchTree" placeholder="Search" id="search-tree" />
      </div>
      <div class="low-toolbar">
        <input type="checkbox" v-if="props.importSpectraMode" @change="selectAll" v-model="selectedAll" id="select-all" />
        <input type="text" v-model="searchList" placeholder="Search" id="search-list" />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.material-symbols-outlined {
  font-size: 1.5rem;
  color: #555;
}

.browser {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 600px;
  border: 1px solid var(--theme-border-color);
  border-radius: 5px;

  .toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 50px;
    background-color: var(--theme-panel);
    padding: 0 1rem;
    border-bottom: 1px solid var(--theme-border-color);

    .tooltip {
      pointer-events: none;
      display: block;
      opacity: 0;
      position: absolute;
      width: max-content;
      top: -1.5rem;
      background-color: var(--theme-panel);
      color: var(--theme-text-color);
      z-index: 100;
      font-size: 1rem;
      transition: opacity 0.2s;
    }

    button:hover .tooltip {
      display: block;
      opacity: 1;
    }

    label:hover .tooltip {
      display: block;
      opacity: 1;
    }

    button,
    .button {
      position: relative;
      background: none;
      border: none;
      cursor: pointer;
      border-radius: 5px;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0.5rem;

      &:hover:not(:disabled) {
        background-color: var(--theme-panel-secondary);
      }

      &:active:not(:disabled) {
        background-color: var(--theme-panel-tertiary);
      }

      &:disabled {
        cursor: default;
        opacity: 0.5;
      }
    }

    .toolbar__left,
    .toolbar__right {
      display: flex;
      align-items: center;

      .path {
        overflow-x: auto;
        display: flex;
        gap: 0.2rem;
        align-items: center;
        user-select: none;

        button {
          font-size: 1rem;
        }
      }
    }
  }

  .browser-content {
    position: relative;
    display: grid;
    grid-template-columns: auto 1fr;
    grid-template-rows: 1fr;
    flex: 1;
    overflow: hidden;

    .tree {
      min-width: 200px;
      overflow-y: auto;
      border-right: 1px solid var(--theme-border-color);
    }

    .files {
      min-width: 200px;
      flex: 1;
      overflow-y: auto;
    }

    .low-toolbar {
      display: flex;
      height: 50px;
      width: 100%;
      padding: 0.5rem;
      background-color: var(--theme-panel);
      gap: 0.5rem;
      border-top: 1px solid var(--theme-border-color);

      input[type='text'] {
        flex: 1;
        padding: 0.5rem;
        border-radius: 5px;
        background: var(--theme-background);
        border: 1px solid var(--theme-border-color);
      }

      input[type='checkbox'] {
        cursor: pointer;
        transform: scale(1.2);
      }
    }
  }
}
</style>
